import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import styles from './FeedbackManagement.module.css';
import { 
  MessageSquare, Filter, Download, X, Send, 
  AlertCircle, CheckCircle, Clock, BarChart3 
} from 'lucide-react';
import ContextualHelp from '../components/ContextualHelp';

interface Feedback {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  pageScope: string;
  category: string;
  message: string;
  priority: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  responses: FeedbackResponse[];
}

interface FeedbackResponse {
  id: number;
  adminUserId: number;
  adminName: string;
  responseText: string;
  respondedAt: string;
}

interface Stats {
  total: number;
  open: number;
  inReview: number;
  completed: number;
  byCategory: { category: string; count: number }[];
  byPage: { page: string; count: number }[];
}

const FeedbackManagement: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [responseText, setResponseText] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    pageScope: '',
    priority: ''
  });

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, [filters]);

  const fetchFeedbacks = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.pageScope) params.append('pageScope', filters.pageScope);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await axios.get(`/feedback?${params.toString()}`);
      setFeedbacks(response.data);
    } catch (error) {
      console.error('Failed to fetch feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/feedback/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleUpdatePriority = async (id: number, priority: string) => {
    try {
      await axios.put(`/feedback/${id}/priority`, { priority });
      fetchFeedbacks();
    } catch (error) {
      console.error('Failed to update priority:', error);
      alert('Failed to update priority');
    }
  };

  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      await axios.put(`/feedback/${id}/status`, { status });
      fetchFeedbacks();
      if (selectedFeedback?.id === id) {
        setSelectedFeedback(prev => prev ? { ...prev, status } : null);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const handleAddResponse = async () => {
    if (!selectedFeedback || !responseText.trim()) return;

    setIsResponding(true);
    try {
      await axios.post(`/feedback/${selectedFeedback.id}/respond`, {
        responseText: responseText.trim()
      });
      setResponseText('');
      fetchFeedbacks();
      
      // Refresh the selected feedback to show new response
      const response = await axios.get(`/feedback/${selectedFeedback.id}`);
      setSelectedFeedback(response.data);
    } catch (error) {
      console.error('Failed to add response:', error);
      alert('Failed to add response');
    } finally {
      setIsResponding(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      if (filters.pageScope) params.append('pageScope', filters.pageScope);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await axios.get(`/feedback/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export CSV:', error);
      alert('Failed to export feedback data');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open':
        return <AlertCircle size={16} className={styles.statusIconOpen} />;
      case 'In Review':
        return <Clock size={16} className={styles.statusIconReview} />;
      case 'Completed':
        return <CheckCircle size={16} className={styles.statusIconCompleted} />;
      default:
        return null;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Open':
        return styles.statusOpen;
      case 'In Review':
        return styles.statusReview;
      case 'Completed':
        return styles.statusCompleted;
      default:
        return '';
    }
  };

  const getPriorityClass = (priority: string | null) => {
    switch (priority) {
      case 'High':
        return styles.priorityHigh;
      case 'Medium':
        return styles.priorityMedium;
      case 'Low':
        return styles.priorityLow;
      default:
        return styles.priorityNone;
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading feedback...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <MessageSquare size={32} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <h1>Feedback Management</h1>
              <ContextualHelp featureArea="feedback" />
            </div>
            <p className={styles.subtitle}>Review and respond to user feedback</p>
          </div>
        </div>
        <button className={styles.exportBtn} onClick={handleExportCSV}>
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total Feedback</div>
            <div className={styles.statValue}>{stats.total}</div>
          </div>
          <div className={`${styles.statCard} ${styles.statOpen}`}>
            <div className={styles.statLabel}>Open</div>
            <div className={styles.statValue}>{stats.open}</div>
          </div>
          <div className={`${styles.statCard} ${styles.statReview}`}>
            <div className={styles.statLabel}>In Review</div>
            <div className={styles.statValue}>{stats.inReview}</div>
          </div>
          <div className={`${styles.statCard} ${styles.statCompleted}`}>
            <div className={styles.statLabel}>Completed</div>
            <div className={styles.statValue}>{stats.completed}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersBar}>
        <Filter size={20} style={{ color: 'var(--text-primary)' }} />
        <span>Filters:</span>
        
        <select 
          value={filters.status} 
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Statuses</option>
          <option value="Open">Open</option>
          <option value="In Review">In Review</option>
          <option value="Completed">Completed</option>
        </select>

        <select 
          value={filters.category} 
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Categories</option>
          <option value="Bug">Bug</option>
          <option value="Feature Request">Feature Request</option>
          <option value="UI/UX">UI/UX</option>
          <option value="Performance">Performance</option>
          <option value="Other">Other</option>
        </select>

        <select 
          value={filters.priority} 
          onChange={(e) => handleFilterChange('priority', e.target.value)}
          className={styles.filterSelect}
        >
          <option value="">All Priorities</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>

        {(filters.status || filters.category || filters.priority) && (
          <button 
            className={styles.clearFilters}
            onClick={() => setFilters({ status: '', category: '', pageScope: '', priority: '' })}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Feedback List */}
      <div className={styles.feedbackList}>
        {feedbacks.length === 0 ? (
          <div className={styles.emptyState}>
            <MessageSquare size={48} />
            <p>No feedback found</p>
          </div>
        ) : (
          feedbacks.map(feedback => (
            <div 
              key={feedback.id}
              className={styles.feedbackCard}
              onClick={() => setSelectedFeedback(feedback)}
            >
              <div className={styles.feedbackHeader}>
                <div className={styles.feedbackMeta}>
                  <span className={styles.feedbackId}>#{feedback.id}</span>
                  <span className={`${styles.status} ${getStatusClass(feedback.status)}`}>
                    {getStatusIcon(feedback.status)}
                    {feedback.status}
                  </span>
                  <span className={styles.category}>{feedback.category}</span>
                  {feedback.priority && (
                    <span className={`${styles.priority} ${getPriorityClass(feedback.priority)}`}>
                      {feedback.priority}
                    </span>
                  )}
                </div>
                <div className={styles.feedbackDate}>
                  {new Date(feedback.createdAt).toLocaleDateString()}
                </div>
              </div>
              
              <div className={styles.feedbackBody}>
                <div className={styles.feedbackUser}>
                  <strong>{feedback.userName}</strong> · {feedback.pageScope}
                </div>
                <p className={styles.feedbackMessage}>{feedback.message}</p>
              </div>

              <div className={styles.feedbackFooter}>
                <span className={styles.responseCount}>
                  {feedback.responses.length} {feedback.responses.length === 1 ? 'response' : 'responses'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Feedback Detail Modal */}
      {selectedFeedback && (
        <div className={styles.modalOverlay} onClick={() => setSelectedFeedback(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2>Feedback #{selectedFeedback.id}</h2>
                <p className={styles.modalSubtitle}>
                  {selectedFeedback.userName} · {selectedFeedback.userEmail}
                </p>
              </div>
              <button 
                className={styles.modalClose}
                onClick={() => setSelectedFeedback(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              {/* Feedback Details */}
              <div className={styles.detailsSection}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Page:</span>
                  <span>{selectedFeedback.pageScope}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Category:</span>
                  <span>{selectedFeedback.category}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status:</span>
                  <select 
                    value={selectedFeedback.status}
                    onChange={(e) => handleUpdateStatus(selectedFeedback.id, e.target.value)}
                    className={styles.detailSelect}
                  >
                    <option value="Open">Open</option>
                    <option value="In Review">In Review</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Priority:</span>
                  <select 
                    value={selectedFeedback.priority || ''}
                    onChange={(e) => handleUpdatePriority(selectedFeedback.id, e.target.value)}
                    className={styles.detailSelect}
                  >
                    <option value="">Not Set</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Submitted:</span>
                  <span>{new Date(selectedFeedback.createdAt).toLocaleString()}</span>
                </div>
              </div>

              {/* Feedback Message */}
              <div className={styles.messageSection}>
                <h3>Message</h3>
                <div className={styles.messageBox}>
                  {selectedFeedback.message}
                </div>
              </div>

              {/* Responses */}
              <div className={styles.responsesSection}>
                <h3>Responses ({selectedFeedback.responses.length})</h3>
                
                {selectedFeedback.responses.map(response => (
                  <div key={response.id} className={styles.responseCard}>
                    <div className={styles.responseHeader}>
                      <strong>{response.adminName}</strong>
                      <span className={styles.responseDate}>
                        {new Date(response.respondedAt).toLocaleString()}
                      </span>
                    </div>
                    <div className={styles.responseBody}>
                      {response.responseText}
                    </div>
                  </div>
                ))}

                {/* Add Response Form */}
                <div className={styles.addResponseSection}>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your response..."
                    rows={4}
                    className={styles.responseTextarea}
                  />
                  <button
                    onClick={handleAddResponse}
                    disabled={!responseText.trim() || isResponding}
                    className={styles.sendBtn}
                  >
                    <Send size={18} />
                    {isResponding ? 'Sending...' : 'Send Response'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackManagement;
